import { useEffect } from 'react'

import { ButtonGroup, FormattedMessage, useLoading } from '@subaio/components'

import type { OnLogEvent } from 'lib/container'

import type {
	Handlers,
	OpportunityCarInsuranceStep,
	OpportunityCarInsuranceStepType,
} from 'containers/OpportunityCarInsurance/handlers'

import { ButtonLink, ButtonSolid } from 'components/Buttons'
import { Card } from 'components/Card'
import { HeaderIconCarInsurance, IconInfo } from 'components/Icons'
import { Header, HeaderIconWrapper } from 'components/Layout'

import { Title } from '../Title'

type Props = TaggedType<OpportunityCarInsuranceStep, OpportunityCarInsuranceStepType.Overview> & {
	onShowHowItWorks: Handlers['onShowHowItWorks']
	onShowPersonalForm: Handlers['onShowPersonalForm']
	onShowProductList: Handlers['onShowProductList']
	onLogScopedEvent: OnLogEvent
}

export const Overview = ({ onShowHowItWorks, onShowProductList, onLogScopedEvent }: Props) => {
	const isLoading = useLoading()

	useEffect(() => {
		if (!isLoading) {
			onLogScopedEvent('OVERVIEW_HAS_SERVICES')
		}
	}, [isLoading, onLogScopedEvent])

	return (
		<Card>
			<Header>
				<HeaderIconWrapper>
					<HeaderIconCarInsurance />
				</HeaderIconWrapper>
				<Title>
					<FormattedMessage
						id='opportunityCarInsurance.overview.empty.title'
						defaultMessage="Compare your car insurance with ABN AMRO's to see if you can save"
						placeholderLines={3}
					/>
				</Title>
			</Header>
			<p role='text'>
				<FormattedMessage
					id='opportunityCarInsurance.overview.empty.description'
					defaultMessage='Wondering if you can save? Follow the steps and find out within minutes.'
				/>
			</p>
			<ButtonGroup column spacing={12}>
				<ButtonGroup left>
					<ButtonLink
						label={
							<FormattedMessage
								id='opportunityCarInsurance.overview.buttonExplanation'
								defaultMessage='How does it work?'
							/>
						}
						icon={<IconInfo />}
						tracking={{
							type: 'textlink',
							label: 'How does it work?',
						}}
						onClick={() => onShowHowItWorks()}
					/>
				</ButtonGroup>
				<ButtonGroup center>
					<ButtonSolid
						label={<FormattedMessage id='opportunityCarInsurance.overview.buttonContinue' defaultMessage='Continue' />}
						onClick={() => onShowProductList()}
					/>
				</ButtonGroup>
			</ButtonGroup>
		</Card>
	)
}
